/**
 * The Workshop's Review tab (Wave B #2).
 *
 * Replaces FindingsPanel, which showed only deterministic WIRING checks
 * and never looked at the prose the persona actually sends. Those checks
 * survive here as a demoted subsection — they still catch real
 * misconfiguration and still feed the top-bar health chip.
 *
 * Two LLM checks with different lifetimes get separate run controls:
 * the prompt review invalidates on every block edit, while workflow fit
 * only goes stale when a bound workflow or the blocks change. One
 * button for both would re-pay for the workflow corpus every time.
 *
 * Nothing here runs automatically — an LLM call costs latency and money.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react'

import { Button } from '@/components/ui/Button'
import { useComposerStore } from '@/stores/composerStore'
import { useFeature } from '@/hooks/useFeature'
import {
  usePersonaReview, useRunReview, useReviewStatus,
} from '@/hooks/usePersonaReview'
import { useSkillCatalog } from '@/hooks/useSkillCatalog'
import { useWorkflows } from '@/hooks/useWorkflows'
import { formatRelativeTime } from '@/utils/relativeTime'
import type { ReviewFinding, ReviewKindState } from '@/types/personaReview'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'
import { applyProposal, validateProposal } from './applyCoachProposal'
import type { ProposalContext } from './applyCoachProposal'
import { focusTargetAcrossSnapshots } from './composedPromptTargets'
import { useFocusedBlock } from './FocusedBlockContext'
import { blockFieldLabel } from './blockTaxonomy'

interface Props {
  personaId: string
  wiringFindings: Finding[]
  onJump: (loc: FindingLocation) => void
  onFixWithCoach: (text: string) => void
}

const SEVERITY_RANK = { error: 0, warning: 1, info: 2 } as const

// Renders both ReviewFinding['severity'] ('info'|'warning'|'error', LLM
// review findings) and the wiring Finding['severity'] ('error'|'warning'
// only, bundleFindings.ts) — the latter is a subset of the former, so one
// helper covers both without a cast.
function SeverityIcon({ severity }: { severity: ReviewFinding['severity'] }) {
  if (severity === 'error') return <AlertCircle className="size-4 shrink-0 text-destructive" />
  if (severity === 'warning') return <AlertTriangle className="size-4 shrink-0 text-warning" />
  return <Info className="size-4 shrink-0 text-info" />
}

function usageLine(kindState: ReviewKindState): string | null {
  const r = kindState.result
  if (!r) return null
  const tokens = (r.prompt_tokens + r.completion_tokens).toLocaleString()
  const parts = [`v${r.bundle_version}`]
  if (r.created_at) {
    const ms = Date.parse(r.created_at)
    if (!Number.isNaN(ms)) parts.push(formatRelativeTime(ms))
  }
  parts.push(`~${tokens} tokens`)
  // Cost is omitted at zero on purpose: llm_pricing.compute_usd
  // exact-matches an 8-row model table, so an unlisted model prices at 0
  // and "$0.00" would be a lie rather than a measurement.
  if (r.total_cost_usd > 0) parts.push(`$${r.total_cost_usd.toFixed(4)}`)
  return parts.join(' · ')
}

/**
 * The reviewed-versus-current line.
 *
 * Two independent things can date a review, and the server only knows
 * about one of them: it compares SAVED bundles, so an unsaved draft never
 * moves its `stale` flag. Without the draft clause the panel goes on
 * asserting the review is current while the user rewrites the prompt
 * underneath it (spec §8 names both clauses).
 */
function stalenessLine(
  kindState: ReviewKindState | undefined,
  currentVersion: number | null | undefined,
  draftDirty: boolean,
): string | null {
  const r = kindState?.result
  if (!r) return null
  const reasons: string[] = []
  if (kindState?.stale) reasons.push(`you're editing v${currentVersion ?? '—'} draft`)
  if (draftDirty) reasons.push('you have unsaved edits')
  if (reasons.length === 0) return null
  return `Reviewed v${r.bundle_version} · ${reasons.join(' · ')}`
}

interface RowProps {
  finding: ReviewFinding
  /**
   * Whether this finding's `bundle_index` values still address the array
   * they were computed against. False once a Save superseded the reviewed
   * bundle — see `supersededBy` below.
   */
  indexable: boolean
  ctx: ProposalContext
  store: ReturnType<typeof useComposerStore.getState>
  onFixWithCoach: (text: string) => void
  openBlock: (t: NonNullable<ReturnType<typeof focusTargetAcrossSnapshots>>) => void
}

function FindingRow({ finding, indexable, ctx, store, onFixWithCoach, openBlock }: RowProps) {
  const proposal = finding.proposal
  const coachProposal = proposal
    ? {
        op: 'updateBlock' as const,
        category: proposal.category as never,
        content: proposal.content,
        rationale: proposal.rationale,
        bundle_index: proposal.bundle_index,
      }
    : null
  const validation = coachProposal && indexable ? validateProposal(coachProposal, ctx) : null

  const anchorFor = (bundleIndex: number) => {
    const target = focusTargetAcrossSnapshots(
      ctx.savedBlocks ?? [], ctx.liveBlocks ?? [], bundleIndex,
    )
    const label = target ? blockFieldLabel(target.category, target.ordinal) : 'Deleted block'
    return { target, label, key: `${finding.id}-${bundleIndex}` }
  }

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-start gap-2">
        <SeverityIcon severity={finding.severity} />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium">{finding.issue}</p>
          <p className="text-sm text-muted-foreground">{finding.suggestion}</p>

          {indexable && finding.block_indices.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {finding.block_indices.map((bi) => {
                const a = anchorFor(bi)
                return (
                  <button
                    key={a.key}
                    type="button"
                    data-testid="review-block-anchor"
                    disabled={!a.target}
                    onClick={() => a.target && openBlock(a.target)}
                    className="rounded border border-border px-1.5 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                  >
                    {a.label}
                  </button>
                )
              })}
            </div>
          )}

          {finding.layer_id && (
            <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground">
              system: {finding.layer_id}
            </span>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            {coachProposal && validation?.appliable && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => applyProposal(coachProposal, store, ctx)}
              >
                Apply
              </Button>
            )}
            {coachProposal && validation && !validation.appliable && (
              <span className="text-xs text-muted-foreground">{validation.reason}</span>
            )}
            {coachProposal && !indexable && (
              <span className="text-xs text-muted-foreground">
                Re-run the review to apply this.
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onFixWithCoach(
                `Help me fix this review finding: ${finding.issue}\n\n${finding.suggestion}`,
              )}
            >
              Fix with Coach
            </Button>
            {finding.workflow_ref && (
              <Link
                className="text-xs underline"
                to={`/workflows/${finding.workflow_ref.workflow_id}?tab=${finding.workflow_ref.tab}`}
              >
                Open workflow config →
              </Link>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

export function ReviewPanel({ personaId, wiringFindings, onJump, onFixWithCoach }: Props) {
  const { data, isLoading, isError: loadFailed } = usePersonaReview(personaId)
  const { data: status } = useReviewStatus(personaId)
  const run = useRunReview(personaId)

  const serverBundle = useComposerStore((s) => s.serverBundle)
  const liveBundle = useComposerStore((s) => s.draftBundle ?? s.serverBundle)
  const store = useComposerStore((s) => s)
  const draftDirty = (store.dirtyPaths?.size ?? 0) > 0
  const { open: openBlock } = useFocusedBlock()
  const { data: skills } = useSkillCatalog()
  // The workflows router rides workflow_engine; without it the catalog
  // query would 404 — proposal validation just can't check workflow
  // installs then (mirrors CoachPanel's established gating).
  const workflowEngineOn = useFeature('workflow_engine')
  const { data: workflows } = useWorkflows({ enabled: workflowEngineOn })

  const ctx = useMemo(() => ({
    skills: skills ?? [],
    workflows: workflows ?? [],
    bundle: liveBundle,
    savedBlocks: serverBundle?.blocks ?? [],
    liveBlocks: liveBundle?.blocks ?? [],
  }), [skills, workflows, liveBundle, serverBundle])

  if (status && status.available === false) {
    return (
      <section className="p-4 text-sm text-muted-foreground" aria-label="Review unavailable">
        <p className="font-medium text-foreground">Review unavailable</p>
        <p className="mt-1">{status.reason ?? 'No language model is configured.'}</p>
      </section>
    )
  }

  const promptState = data?.prompt
  const workflowState = data?.workflow_fit
  const currentVersion = data?.current_bundle_version
  const currentBundleId = data?.current_bundle_id ?? null
  const savedBundleId = serverBundle?.id ?? null
  const busy = run.isPending || isLoading

  /**
   * A review's `bundle_index` values are only meaningful against the array
   * they were computed from. `commitDraft` replaces `serverBundle` on every
   * Save, so a review taken against v41 can outlive its bundle: index 2 may
   * have been an extra `trait` then and be `constraints` now. The index
   * still RESOLVES, which is what makes it dangerous —
   * `focusTargetAcrossSnapshots` is handed the current saved blocks and has
   * no way to notice. `bundle_id` is the only honest signal, so every
   * index-based affordance is withheld until the review is re-run.
   *
   * BOTH ids must agree, because they go stale at different moments:
   * `commitDraft` swaps the store's `serverBundle` synchronously, while
   * `current_bundle_id` only catches up when the invalidated GET returns.
   * Trusting the wire alone leaves a refetch-long window in which the two
   * old ids match and Apply is wrongly offered; trusting the store alone
   * misses a Save made in another tab. Disagreement — in either
   * direction — withholds the index.
   */
  const isIndexable = (kindState: ReviewKindState | undefined): boolean => {
    const rid = kindState?.result?.bundle_id
    if (!rid) return false
    if (savedBundleId && rid !== savedBundleId) return false
    if (currentBundleId && rid !== currentBundleId) return false
    return Boolean(savedBundleId || currentBundleId)
  }

  const sorted = (kindState: ReviewKindState | undefined): ReviewFinding[] =>
    [...(kindState?.result?.findings ?? [])].sort(
      (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
    )

  const promptFindings = sorted(promptState)
  const workflowFindings = sorted(workflowState)
  const promptIndexable = isIndexable(promptState)
  const workflowIndexable = isIndexable(workflowState)

  const renderRows = (findings: ReviewFinding[], indexable: boolean) =>
    findings.map((finding) => (
      <FindingRow
        key={finding.id}
        finding={finding}
        indexable={indexable}
        ctx={ctx}
        store={store}
        onFixWithCoach={onFixWithCoach}
        openBlock={openBlock}
      />
    ))

  const supersededNotice = (kindState: ReviewKindState | undefined) =>
    kindState?.result && !isIndexable(kindState) ? (
      <p className="text-xs text-warning" data-testid="review-superseded">
        This review ran against an earlier saved version, so it can no longer
        point at specific blocks. Re-run the review to apply its suggestions.
      </p>
    ) : null

  const promptStale = stalenessLine(promptState, currentVersion, draftDirty)

  return (
    <section className="space-y-4 p-3" aria-label="Persona review">
      {loadFailed && (
        <p className="text-xs text-destructive" data-testid="review-load-error" role="alert">
          Couldn&apos;t load your saved review. Close the tab and try again.
        </p>
      )}
      {run.isError && (
        <p className="text-xs text-destructive" data-testid="review-run-error" role="alert">
          Review failed: {run.error?.message || 'the model call did not complete.'} Try again.
        </p>
      )}

      <header className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Prompt review</h3>
          <Button size="sm" onClick={() => run.mutate('prompt')} disabled={busy}>
            {run.isPending ? 'Reviewing…' : 'Review now'}
          </Button>
        </div>
        {promptState?.result && (
          <p className="text-xs text-muted-foreground" data-testid="review-usage">
            Reviewed {usageLine(promptState)}
          </p>
        )}
        {promptStale && (
          <p className="text-xs text-warning" data-testid="review-stale">{promptStale}</p>
        )}
        {supersededNotice(promptState)}
        {isLoading && (
          <p className="text-xs text-muted-foreground" data-testid="review-loading">
            Loading your last review…
          </p>
        )}
        {!isLoading && !loadFailed && !promptState?.result && (
          <p className="text-xs text-muted-foreground">
            Not reviewed yet. Reviewing reads your prompt blocks and costs a
            model call.
          </p>
        )}
        {promptState?.result && promptFindings.length === 0 && (
          <p
            className="flex items-center gap-1.5 pt-1 text-xs text-success"
            data-testid="review-clean"
          >
            <CheckCircle2 className="size-4 shrink-0" />
            Your prompt looks clean — nothing to flag in this version.
          </p>
        )}
      </header>

      {promptFindings.length > 0 && (
        <ul className="space-y-3">{renderRows(promptFindings, promptIndexable)}</ul>
      )}

      <section className="space-y-2 border-t border-border pt-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">Workflow fit</h4>
          {/*
            `=== true` rather than `!== false`: `workflowState` is undefined
            while the initial GET is in flight, and `undefined !== false`
            renders an enabled button on deployments where the check is
            unavailable — one click of which used to buy a billed LLM call
            reviewing an empty corpus.
          */}
          {workflowState?.available === true && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => run.mutate('workflow_fit')}
              disabled={busy}
            >
              {workflowState.result ? 'Re-check workflow fit' : 'Check workflow fit'}
            </Button>
          )}
        </div>
        {workflowState?.available === false && (
          <p className="text-xs text-muted-foreground">{workflowState.unavailable_reason}</p>
        )}
        {workflowState?.available === true && workflowState.result && workflowState.stale && (
          <p className="text-xs text-warning">
            Workflow check out of date — a bound workflow changed.
          </p>
        )}
        {workflowState?.available === true && supersededNotice(workflowState)}
        {workflowState?.available === true
          && workflowState.result
          && workflowFindings.length === 0 && (
          <p
            className="flex items-center gap-1.5 text-xs text-success"
            data-testid="review-workflow-clean"
          >
            <CheckCircle2 className="size-4 shrink-0" />
            Your prompt and your bound workflows agree.
          </p>
        )}
        {workflowFindings.length > 0 && (
          <ul className="space-y-3">{renderRows(workflowFindings, workflowIndexable)}</ul>
        )}
      </section>

      {wiringFindings.length > 0 && (
        <section className="space-y-2 border-t border-border pt-3">
          <h4 className="text-sm font-semibold">Wiring</h4>
          <ul className="space-y-1">
            {wiringFindings.map((f) => (
              <li key={f.id} className="flex items-start gap-2 text-sm">
                <SeverityIcon severity={f.severity} />
                <span className="min-w-0 flex-1">{f.message}</span>
                <button
                  type="button"
                  className="text-xs underline"
                  onClick={() => onJump(f.location)}
                >
                  Show
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  )
}
